package io.horion.auth.domain.models

import androidx.compose.runtime.*

data class AppStrings(
    // ── Général ──────────────────────────────────────────────────────────────
    val loading: String,
    val errorGeneric: String,

    // ── VerificationBanner ────────────────────────────────────────────────────
    val bannerTitle: String,
    val bannerMessageEmail: String,
    val bannerMessagePhone: String,
    val bannerVerifyButton: String,

    // ── Méthode de connexion (switcher email ↔ téléphone) ─────────────────────
    val byEmail: String,
    val byPhone: String,
    val switchToPhone: String,
    val switchToEmail: String,

    // ── Page de connexion (Index) ─────────────────────────────────────────────
    val loginTitle: String,
    val loginSubtitle: String,
    val loginButton: String,
    val forgotPassword: String,
    val noAccount: String,
    val signUpNow: String,
    val emailPlaceholder: String,
    val passwordPlaceholder: String,
    val phonePlaceholder: String,

    // ── Page d'inscription (Register) ─────────────────────────────────────────
    val registerTitle: String,
    val registerSubtitle: String,
    val registerButton: String,
    val alreadyHaveAccount: String,
    val signInNow: String,
    val passwordHint: String,
    val passwordConfirmHint: String,
    val rememberMe: String,
    val nextButton: String,

    // ── Champ OTP (Register + Reset) ──────────────────────────────────────────
    val verifyTitle: String,
    val verifySubtitle: String,
    val sendCode: String,
    val resendCode: String,
    val resendWithTimer: (Int) -> String,
    val otpPlaceholder: String,
    val otpWaitPlaceholder: String,

    // ── Page de réinitialisation (ResetPassword) ──────────────────────────────
    val resetTitle: String,
    val resetSubtitle: String,
    val forgetSubtitle: String,
    val resetButton: String,
    val resetViaEmail: String,
    val resetViaPhone: String,
    val newPasswordPlaceholder: String,
    val newPasswordWaitPlaceholder: String,
    val resetSuccessTitle: String,
    val resetSuccessSubtitle: String,

    // ── Page Profil ────────────────────────────────────────────────────────────
    val profileTitle: String,
    val profileSectionInfo: String,
    val profileEditButton: String,
    val profileSaveButton: String,
    val profileCancelButton: String,
    val profileUpdateSuccess: String,
    val emailLabel: String,
    val phoneLabel: String,
    val emptyField: String,
    val logout: String,
    val profileName: String,
    val addressLabel: String,

    // ── Sidebar ────────────────────────────────────────────────────────────────
    val sidebarHome: String,
    val sidebarPersonalInfo: String,
    val sidebarSecurity: String,
    val sidebarHelp: String,
    val sidebarHelpSubtitle: String,
    val sidebarHelpCenter: String,

    // ── Profile Header ─────────────────────────────────────────────────────────
    val profileHeaderSubtitle: String,
    val profileHeaderNotifications: String,

    // ── Profile Home Section ───────────────────────────────────────────────────
    val lastLogin: String,
    val lastLoginDate: String,
    val lastLoginLocation: String,
    val roleLabel: String,
    val roleAdmin: String,
    val accountStatusLabel: String,
    val accountActive: String,

    // ── Section Informations personnelles ──────────────────────────────────────
    val personalInfoTitle: String,
    val personalInfoSubtitle: String,
    val firstNameLabel: String,
    val lastNameLabel: String,
    val passwordChangeSuccess: String,
    val currentPasswordLabel: String,

    // ── Section Sécurité ───────────────────────────────────────────────────────
    val securityTitle: String,
    val securitySubtitle: String,
    val securityPassword: String,
    val securityPasswordSubtitle: String,
    val securityTwoFactor: String,
    val securityTwoFactorEnabled: String,
    val securityTwoFactorDisabled: String,
    val securityDevices: String,
    val securityDevicesSubtitle: String,
    val securityHistory: String,
    val securityHistorySubtitle: String,

    // ── Titres de pages (document.title) ─────────────────────────────────────
    val pageTitleLogin: String,
    val pageTitleRegister: String,
    val pageTitleReset: String,
    val pageTitleForget: String,
    val pageTitleProfile: String,
    val pageTitleVerify: String,
)

val FrenchStrings = AppStrings(
    // Général
    loading = "Chargement…",
    errorGeneric = "Une erreur est survenue. Veuillez réessayer.",

    // Vérification
    bannerTitle = "Compte non vérifié",
    bannerMessageEmail = "Vérifie ton e-mail pour accéder à toutes les fonctionnalités",
    bannerMessagePhone = "Vérifie ton téléphone pour accéder à toutes les fonctionnalités",
    bannerVerifyButton = "Vérifier",

    // Switcher
    byEmail = "Par e-mail",
    byPhone = "Par téléphone",
    switchToPhone = "Utiliser un téléphone",
    switchToEmail = "Utiliser un e-mail",

    // Connexion
    loginTitle = "Connexion",
    loginSubtitle = "Bienvenue ! Connectez-vous pour continuer.",
    loginButton = "SE CONNECTER",
    forgotPassword = "Mot de passe oublié ?",
    noAccount = "Pas encore de compte ? ",
    signUpNow = "S'inscrire",
    emailPlaceholder = "Adresse e-mail",
    passwordPlaceholder = "Mot de passe",
    phonePlaceholder = "Numéro de téléphone",

    // Inscription
    registerTitle = "Créer un compte",
    registerSubtitle = "Rejoignez Orion en quelques secondes.",
    registerButton = "S'INSCRIRE",
    alreadyHaveAccount = "Vous avez déjà un compte ? ",
    signInNow = "Se connecter",
    passwordHint = "Mot de passe (6 caractères min.)",
    passwordConfirmHint = "Confirmer le mot de passe",
    rememberMe = "Se souvenir de moi",
    nextButton = "SUIVANT",

    // OTP
    verifyTitle = "Vérification du compte",
    verifySubtitle = "Pour sécuriser votre accès, veuillez saisir le code de validation que vous venez de recevoir ",
    sendCode = "Envoyer le code",
    resendCode = "Renvoyer",
    resendWithTimer = { s -> "Renvoyer (${s}s)" },
    otpPlaceholder = "Code à 6 chiffres",
    otpWaitPlaceholder = "Envoie le code ",

    // Réinitialisation
    resetTitle = "Mot de passe oublié ?",
    resetSubtitle = "Saisis ton e-mail ou téléphone pour recevoir un code de réinitialisation.",
    forgetSubtitle = "Saisis le nouveau mots de passe et le code.",
    resetButton = "RÉINITIALISER LE MOT DE PASSE",
    resetViaEmail = "Via e-mail",
    resetViaPhone = "Via téléphone",
    newPasswordPlaceholder = "Nouveau mot de passe",
    newPasswordWaitPlaceholder = "Saisis  le code reçu",
    resetSuccessTitle = "Mot de passe réinitialisé !",
    resetSuccessSubtitle = "Redirection vers la connexion…",

    // Profile
    profileTitle = "Mon profil",
    profileSectionInfo = "Informations personnelles",
    profileEditButton = "Modifier",
    profileSaveButton = "Enregistrer",
    profileCancelButton = "Annuler",
    profileUpdateSuccess = "Profil mis à jour avec succès !",
    emailLabel = "Email",
    phoneLabel = "Téléphone",
    emptyField = "Non renseigné",
    logout = "Se déconnecter",
    profileName = "Nom complet",
    addressLabel = "Adresse",

    // Sidebar
    sidebarHome = "Accueil",
    sidebarPersonalInfo = "Informations personnelles",
    sidebarSecurity = "Sécurité et connexion",
    sidebarHelp = "Besoin d'aide ?",
    sidebarHelpSubtitle = "Consultez notre centre d'aide ou contactez le support.",
    sidebarHelpCenter = "Centre d'aide",

    // Profile Header
    profileHeaderSubtitle = "Vue d'ensemble de votre compte",
    profileHeaderNotifications = "Notifications",

    // Section Informations personnelles
    personalInfoTitle = "Informations personnelles",
    personalInfoSubtitle = "Gérez vos informations personnelles",

    // Section Sécurité
    securityTitle = "Sécurité et connexion",
    securitySubtitle = "Gérez votre sécurité et vos connexions",
    securityPassword = "Mot de passe",
    securityPasswordSubtitle = "Modifiez votre mot de passe",
    securityTwoFactor = "Double authentification",
    securityTwoFactorEnabled = "Activée",
    securityTwoFactorDisabled = "Désactivée",
    securityDevices = "Appareils connectés",
    securityDevicesSubtitle = "3 appareils connectés",
    securityHistory = "Historique de connexion",
    securityHistorySubtitle = "Voir les connexions récentes",
    // Profile Home Section
    lastLogin = "Dernière connexion",
    lastLoginDate = "18 mai 2024 à 14:30",
    lastLoginLocation = "Douala, Cameroun",
    roleLabel = "Rôle",
    roleAdmin = "Admin",
    accountStatusLabel = "Statut du compte",
    accountActive = "Actif",

    // Titres de pages
    pageTitleLogin = "Connexion",
    pageTitleRegister = "Inscription",
    pageTitleReset = "Réinitialisation",
    pageTitleForget = "oublier",
    pageTitleProfile = "Profil",
    pageTitleVerify = "vérifier",
    firstNameLabel = "Nom",
    lastNameLabel = "Prenom",
    passwordChangeSuccess = "Nouveau Mot de Passe",
    currentPasswordLabel = "mot de passe actuel",
)

val EnglishStrings = AppStrings(
    // General
    loading = "Loading…",
    errorGeneric = "Something went wrong. Please try again.",

    // Verification banner
    bannerTitle = "Account not verified",
    bannerMessageEmail = "Verify your email to access all features",
    bannerMessagePhone = "Verify your phone to access all features",
    bannerVerifyButton = "Verify",

    // Switcher
    byEmail = "By email",
    byPhone = "By phone",
    switchToPhone = "Use a phone number",
    switchToEmail = "Use an email",

    // Login
    loginTitle = "Sign In",
    loginSubtitle = "Welcome back! Sign in to continue.",
    loginButton = "SIGN IN",
    forgotPassword = "Forgot password?",
    noAccount = "Don't have an account? ",
    signUpNow = "Sign up",
    emailPlaceholder = "Email address",
    passwordPlaceholder = "Password",
    phonePlaceholder = "Phone number",

    // Register
    registerTitle = "Create an account",
    registerSubtitle = "Join Orion in just a few seconds.",
    registerButton = "SIGN UP",
    alreadyHaveAccount = "Already have an account? ",
    signInNow = "Sign in",
    passwordHint = "Password (min. 6 characters)",
    passwordConfirmHint = "Confirm password",
    rememberMe = "Remember me",
    nextButton = "NEXT",

    // OTP
    verifyTitle = "Account verification",
    verifySubtitle = "To secure your access, please enter the validation code you just received ",
    sendCode = "Send code",
    resendCode = "Resend",
    resendWithTimer = { s -> "Resend (${s}s)" },
    otpPlaceholder = "6-digit code",
    otpWaitPlaceholder = "Send the code ",

    // Reset password
    resetTitle = "Forgot your password?",
    resetSubtitle = "Enter your email or phone number to receive a reset code.",
    forgetSubtitle = "Enter the new password and code.",
    resetButton = "RESET PASSWORD",
    resetViaEmail = "Via email",
    resetViaPhone = "Via phone",
    newPasswordPlaceholder = "New password",
    newPasswordWaitPlaceholder = "Enter the code you received ",
    resetSuccessTitle = "Password reset!",
    resetSuccessSubtitle = "Redirecting to sign in…",

    // Profile
    profileTitle = "My Profile",
    profileSectionInfo = "Personal Information",
    profileEditButton = "Edit",
    profileSaveButton = "Save",
    profileCancelButton = "Cancel",
    profileUpdateSuccess = "Profile updated successfully!",
    emailLabel = "Email",
    phoneLabel = "Phone",
    emptyField = "Not provided",
    logout = "Sign out",
    profileName = "Full Name",
    addressLabel = "Address",
    // Profile Home Section
    lastLogin = "Last login",
    lastLoginDate = "May 18, 2024 at 2:30 PM",
    lastLoginLocation = "Douala, Cameroon",
    roleLabel = "Role",
    roleAdmin = "Admin",
    accountStatusLabel = "Account status",
    accountActive = "Active",

    // Sidebar
    sidebarHome = "Home",
    sidebarPersonalInfo = "Personal Information",
    sidebarSecurity = "Security & Login",
    sidebarHelp = "Need help?",
    sidebarHelpSubtitle = "Visit our help center or contact support.",
    sidebarHelpCenter = "Help Center",

    // Profile Header
    profileHeaderSubtitle = "Your account overview",
    profileHeaderNotifications = "Notifications",

    // Section Personal Info
    personalInfoTitle = "Personal Information",
    personalInfoSubtitle = "Manage your personal information",

    // Section Security
    securityTitle = "Security & Login",
    securitySubtitle = "Manage your security and connections",
    securityPassword = "Password",
    securityPasswordSubtitle = "Change your password",
    securityTwoFactor = "Two-factor authentication",
    securityTwoFactorEnabled = "Enabled",
    securityTwoFactorDisabled = "Disabled",
    securityDevices = "Connected devices",
    securityDevicesSubtitle = "3 connected devices",
    securityHistory = "Login history",
    securityHistorySubtitle = "View recent logins",

    // Page titles
    pageTitleLogin = "Sign In",
    pageTitleRegister = "Register",
    pageTitleReset = "Reset Password",
    pageTitleForget = "forget",
    pageTitleProfile = "Profile",
    pageTitleVerify = "verify",
    firstNameLabel = "firstname",
    lastNameLabel = "lastname",
    passwordChangeSuccess = "New Password",
    currentPasswordLabel = "Actual Password",
)

// Gestionnaire d'état global pour la langue
object AppLanguage {
    var currentCode by mutableStateOf("fr")
    val strings: AppStrings
        get() = if (currentCode == "fr") FrenchStrings else EnglishStrings
}

val LocalStrings = staticCompositionLocalOf { FrenchStrings }